import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

/**
 * The browser's history as the app's back stack, so the system back (Android's
 * gesture, the iOS edge swipe, a desktop's back button) walks back through the app
 * the way a native one does instead of leaving it:
 *
 *   an overlay open (dialog, sheet, menu)  → back closes it
 *   a detail view (a board, an agent chat) → back goes to its list
 *   a tab other than home                  → back goes home
 *   home, installed as an app              → "Press back again to exit"
 *
 * Every entry's state says the whole of where it is — the stack of views from home
 * to the current one, and how many overlays sit on it — so a reload, or a popstate
 * landing several entries down, needs no memory of how it got there. Views are the
 * caller's (`navigateBack`, `onBackView`); overlays are `useBackLayer`.
 *
 * History traversal is asynchronous and a `pushState` issued while one is pending
 * lands in the wrong place (a menu closing as the dialog it opened mounts is the
 * everyday case), so every operation goes through one queue that waits for each
 * traversal's popstate before running the next.
 */

type Entry = {
  bx: 1;
  views: unknown[];
  keys: string[];
  /** Overlays open on this view at this entry. */
  ov: number;
  /** The entry under home in an installed app: landing here is a back at the root. */
  sentinel?: true;
};

export type BackStackView = { key: string; view: unknown };

type Layer = { close: () => void; pushed: boolean; closed: boolean };

const EXIT_WINDOW_MS = 2000;
/** A traversal that never fires popstate (out of range) must not wedge the queue. */
const TRAVERSE_TIMEOUT_MS = 600;

let started = false;
let viewListener: ((view: unknown) => void) | null = null;
let currentKey: string | null = null;
/** Pushed overlay entries, bottom first. */
const layers: Layer[] = [];
const queue: Array<() => boolean> = [];
let waiting: ReturnType<typeof setTimeout> | null = null;
let rearm: ReturnType<typeof setTimeout> | null = null;

const last = <T,>(items: readonly T[]): T | null => items[items.length - 1] ?? null;

const current = (): Entry | null => {
  const state = window.history.state as Partial<Entry> | null;
  return state && state.bx === 1 ? (state as Entry) : null;
};

const entryFor = (stack: BackStackView[], index: number): Entry => ({
  bx: 1,
  views: stack.slice(0, index + 1).map((item) => item.view),
  keys: stack.slice(0, index + 1).map((item) => item.key),
  ov: 0,
});

const standalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  window.matchMedia?.("(display-mode: fullscreen)").matches ||
  (navigator as { standalone?: boolean }).standalone === true;

/** Runs `op`s in order; one returning true started a traversal, and the next waits for its popstate. */
function run(op: () => boolean) {
  queue.push(op);
  drain();
}

function drain() {
  while (waiting === null && queue.length > 0) {
    const op = queue.shift()!;
    if (op()) {
      waiting = setTimeout(() => {
        waiting = null;
        drain();
      }, TRAVERSE_TIMEOUT_MS);
    }
  }
}

function traverse(delta: number): boolean {
  if (delta >= 0) return false;
  window.history.go(delta);
  return true;
}

/** Off the sentinel before anything is pushed, so home is an entry again. */
function leaveSentinel() {
  const entry = current();
  if (entry?.sentinel) window.history.pushState({ ...entry, sentinel: undefined, ov: 0 }, "");
}

function onPopState() {
  const programmatic = waiting !== null;
  if (waiting !== null) clearTimeout(waiting);
  waiting = null;

  const entry = current();
  if (entry) {
    // Everything above the entry landed on is gone: close what is still open.
    while (layers.length > entry.ov) {
      const layer = layers.pop()!;
      if (!layer.closed) {
        layer.closed = true;
        layer.close();
      }
    }
    // Overlays that closed while one above them was open (a menu item opening a
    // dialog: the dialog mounts before the menu goes) still hold entries under it;
    // now they are on top, step over them in the same back.
    let buried = 0;
    while (last(layers)?.closed) {
      layers.pop();
      buried++;
    }
    if (buried > 0) {
      queue.unshift(() => traverse(-buried));
    } else if (entry.ov > layers.length) {
      // An overlay entry whose overlay is not open (a reload, or the forward
      // button): step off it rather than show a back that does nothing.
      queue.unshift(() => traverse(-1));
    } else if (!programmatic) {
      const key = last(entry.keys);
      if (key !== currentKey) {
        currentKey = key;
        viewListener?.(last(entry.views));
      }
      if (entry.sentinel) armExit();
    }
  }
  drain();
}

/**
 * A back at home in the installed app. Staying on the sentinel means the next back
 * leaves (there is nothing under it); after the window, home is pushed again so a
 * back much later asks first once more.
 */
function armExit() {
  toast("Press back again to exit", { duration: EXIT_WINDOW_MS, id: "back-exit" });
  if (rearm) clearTimeout(rearm);
  rearm = setTimeout(() => {
    rearm = null;
    run(() => {
      leaveSentinel();
      return false;
    });
  }, EXIT_WINDOW_MS);
}

/**
 * Takes over history, once, for the view the app opened on. A reload keeps the
 * entries it had; a fresh launch — an icon tap, a deep link, a restored view —
 * builds the parents under it, so back from a restored chat lands on the list.
 */
export function startBackStack(stack: BackStackView[]) {
  if (started) return;
  started = true;
  window.addEventListener("popstate", onPopState);
  const entry = current();
  if (entry && !entry.sentinel) {
    // Nothing is open after a reload; the overlay entries above are forward ones now.
    if (entry.ov > 0) window.history.replaceState({ ...entry, ov: 0 }, "");
    currentKey = last(entry.keys);
    if (currentKey !== last(stack)?.key) navigateBack(stack);
    return;
  }
  if (!entry && standalone()) window.history.replaceState({ ...entryFor(stack, 0), sentinel: true }, "");
  if (entry?.sentinel || (!entry && standalone())) {
    stack.forEach((_, index) => window.history.pushState(entryFor(stack, index), ""));
  } else {
    window.history.replaceState(entryFor(stack, 0), "");
    stack.slice(1).forEach((_, index) => window.history.pushState(entryFor(stack, index + 1), ""));
  }
  currentKey = last(stack)?.key ?? null;
}

/** What a back (or forward) that lands on another view shows. */
export function onBackView(listener: (view: unknown) => void) {
  viewListener = listener;
  return () => {
    if (viewListener === listener) viewListener = null;
  };
}

/**
 * Makes history the stack for a view the app is going to: into a detail pushes,
 * a sideways move (one tab to another, one board to another) replaces, and up to
 * a parent goes back to it — so tabbing around never needs twenty backs to leave.
 * Any overlay open is closed on the way.
 */
export function navigateBack(stack: BackStackView[]) {
  const keys = stack.map((item) => item.key);
  currentKey = last(keys);
  run(() => {
    const entry = current();
    if (!entry || entry.sentinel) {
      if (entry?.sentinel) leaveSentinel();
      else window.history.replaceState(entryFor(stack, 0), "");
      stack.slice(1).forEach((_, index) => window.history.pushState(entryFor(stack, index + 1), ""));
      return false;
    }
    let shared = 0;
    while (shared < keys.length && shared < entry.keys.length && keys[shared] === entry.keys[shared]) shared++;
    // The view entry to land on: the deepest shared one when one stack holds the
    // other, else the first that differs (replaced below).
    const land = Math.max(0, shared === keys.length || shared === entry.keys.length ? shared - 1 : shared);
    const delta = land - (entry.keys.length - 1 + entry.ov);
    queue.unshift(() => {
      const here = current();
      if (here?.keys[land] !== keys[land]) window.history.replaceState(entryFor(stack, land), "");
      for (let index = land + 1; index < stack.length; index++) window.history.pushState(entryFor(stack, index), "");
      return false;
    });
    return traverse(delta);
  });
}

/**
 * Gives an overlay a history entry while `open`: the system back closes it (by
 * calling `close`), and closing it any other way takes its entry back off, so the
 * stack never fills with entries for dialogs long gone.
 */
export function useBackLayer(open: boolean, close: () => void) {
  const closeRef = useRef(close);
  closeRef.current = close;
  useEffect(() => {
    if (!open) return;
    const layer: Layer = { close: () => closeRef.current(), pushed: false, closed: false };
    run(() => {
      if (layer.closed) return false;
      leaveSentinel();
      const entry = current();
      layers.push(layer);
      layer.pushed = true;
      window.history.pushState({ ...(entry ?? { bx: 1, views: [], keys: [] }), ov: layers.length }, "");
      return false;
    });
    return () => {
      if (layer.closed) return;
      layer.closed = true;
      if (!layer.pushed) return;
      // Only the top can come off; a lower one closed first goes with the ones above it.
      run(() => {
        let count = 0;
        while (last(layers)?.closed) {
          layers.pop();
          count++;
        }
        return traverse(-count);
      });
    };
  }, [open]);
}

/**
 * For a Radix root: controlled or not, it gets a back entry while open. Returns
 * the `open` / `onOpenChange` to hand the primitive.
 */
export function useBackClosable({
  open: openProp,
  defaultOpen,
  onOpenChange,
}: {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [inner, setInner] = useState(defaultOpen ?? false);
  const open = openProp ?? inner;
  const change = (next: boolean) => {
    if (openProp === undefined) setInner(next);
    onOpenChange?.(next);
  };
  const changeRef = useRef(change);
  changeRef.current = change;
  useBackLayer(open, () => changeRef.current(false));
  return { open, onOpenChange: change };
}
